// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@pacific/Overrides/Components/ProductPartNumbers";
import React from "react";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { hasEnoughInventory as hasEnoughInventorySelector } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import OverlayBadges from "@pacific/App/Components/Product/OverlayBadge";
import ProductGridContainer, { ProductGridContainerStyle } from "@pacific/App/Components/Product/ProductGridTemplate";
import ProductPriceAndActions, {
    ProductPriceAndActionsStyle,
} from "@pacific/App/Components/Product/ProductPriceAndActions";
import xUseOverlayBadge from "@pacific/App/Hooks/xUseOverlayBadge";
import productDescriptionVariantStyles from "@pacific/App/SharedStyle/ProductDescriptionStyles";
import { xGoogleAnalyticsState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { ProductAvailabilityStyles } from "@pacific/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@pacific/Overrides/Components/ProductContextAvailability";
import { ProductPriceStyles } from "@pacific/Overrides/Components/ProductPrice";
import { color, ellipsis, formatCategories, xScreen } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    // XNG-Change
    session: getSession(state),
    currentBillTo: getCurrentBillToState(state).value,
    productClickEvent: (state as xGoogleAnalyticsState).xGoogleAnalytics.productClickEvent,
    isAuthenticated: xIsAuthenticated(state),
    pageType: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    recommendationId?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    // XNG-Change
    cardView: string;
    menuItemView?: boolean;
    showAddToCartIcon?: boolean;
    gaEvent?: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    imageInfoWrapper?: InjectableCss;
    infoSectionWrapper?: InjectableCss;
    priceAndActionsWrapper?: InjectableCss;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPriceAndActionsStyle?: ProductPriceAndActionsStyle;
    productGridContainer?: ProductGridContainerStyle;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                position: relative;
                height: 308px;
                width: 100%;
            `,
        },
        img: {
            width: 308,
            height: 308,
            transformations: {
                tabletWidth: 346,
                mobileWidth: 328,
            },
            css: css`
                max-width: 100%;
                max-height: 100%;
                height: auto;
            `,
        } as any,
    },
    productDescriptionStyles: {
        ...productDescriptionVariantStyles.medium,
        productDetailDescription: {
            ...productDescriptionVariantStyles.medium?.productDetailDescription,
            css: css`
                ${productDescriptionVariantStyles.medium?.productDetailDescription?.css}
                ${ellipsis(2)}
            `,
        },
        productDetailLink: {
            ...productDescriptionVariantStyles.medium?.productDetailLink,
            css: css`
                ${productDescriptionVariantStyles.medium?.productDetailLink?.css}
                ${ellipsis(2)}
            `,
        },
    },

    priceAndActionsWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-grow: 1;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
};

export const productCarouselMediumProductStyles: ProductCarouselProductStyles = {
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                position: relative;
                height: 88px;
                width: 88px;
                overflow: hidden;
                ${xScreen("lte", "md")} {
                    height: 93px;
                    width: 93px;
                }
            `,
        },
        img: {
            width: "100px",
            height: "100px",
            transformations: {
                tabletWidth: 93,
                mobileWidth: 93,
            },
            css: css`
                &&& {
                    height: auto;
                    max-width: 100%;
                    max-height: 88px;
                    ${xScreen("lte", "md")} {
                        max-height: 93px;
                    }
                }
            `,
        } as any,
    },
    productDescriptionStyles: {
        ...productDescriptionVariantStyles.small,
        productDetailDescription: {
            ...productDescriptionVariantStyles.small?.productDetailDescription,
            css: css`
                ${productDescriptionVariantStyles.small?.productDetailDescription?.css}
                ${ellipsis(2)}
            `,
        },
        productDetailLink: {
            ...productDescriptionVariantStyles.small?.productDetailLink,
            css: css`
                ${productDescriptionVariantStyles.small?.productDetailLink?.css}
                ${ellipsis(2)}
            `,
        },
    },
    imageInfoWrapper: {
        css: css`
            display: flex;
            color: ${color("text")};
            & > div {
                padding: 0;
            }
        `,
    },
    infoSectionWrapper: {
        css: css`
            flex-grow: 1;
            margin-left: 12px;
        `,
    },
    priceAndActionsWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            flex-grow: 1;
            margin-top: 12px;
            color: ${color("text")};
        `,
    },
    productPriceStyles: {
        price: {
            priceText: {
                size: 14,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                },
            },
        },
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    updateProductInfo,
    extendedStyles,
    productInfo,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    recommendationId,
    sendProductRecommendationTracking,
    cardView,
    menuItemView = false,
    canSeePrices,
    showAddToCartIcon = false,
    // XNG-Change
    session,
    currentBillTo,
    productClickEvent,
    isAuthenticated,
    pageType,
    gaEvent,
}: Props) => {
    // XNG-Change
    const [styles] = React.useState(() =>
        mergeToNew(
            cardView === "medium" ? productCarouselMediumProductStyles : productCarouselProductStyles,
            extendedStyles,
        ),
    );

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    // XNG-Change Track Select Item for GA4
    const _GaSelectItem = (elementId: string) => {
        const isPLP = pageType === "ProductListPage";
        const isPDP = pageType === "ProductDetailsPage";
        const list = isPLP ? "Product List Page" : isPDP ? "Product Detail Page" : null;

        if (productClickEvent) {
            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: "select_item",
                item_list_id: pageType,
                item_list_name: list,
                authentication_state: isAuthenticated ? "Authenticated" : "Guest",
                branch_number: (session as any)?.defaultWarehouse?.name || "",
                branch_name: (session as any)?.defaultWarehouse?.description || "",
                sales_rep: (session as any).salesperson?.name || "",
                affiliation: currentBillTo?.companyName || "",
                items: [
                    {
                        item_id: product.productNumber,
                        item_name: product.productTitle,
                        coupon: "",
                        discount: productInfo.pricing
                            ? productInfo.pricing.unitNetPrice < productInfo.pricing.unitListPrice
                                ? productInfo.pricing.unitListPrice - productInfo.pricing.unitNetPrice
                                : 0
                            : 0,
                        index: 0,
                        affiliation: currentBillTo?.companyName || "",
                        item_brand: product.brand?.name || "",
                        ...formatCategories(product.properties?.category),
                        item_list_id: pageType,
                        item_list_name: list,
                        item_variant: "",
                        location_id: (session as any)?.pickUpWarehouse?.id || "",
                        price: productInfo.pricing?.unitNetPrice || 0,
                        quantity: 1,
                    },
                ],
            };
            dataLayer.push(googleAnalyticsData);
            
            // Custom GA event from CMS
            if (gaEvent) {
                const gooogleAnalyticsData = {
                    event: gaEvent,
                    click_text: product.productTitle,
                    destination: product.uri,
                    elementId,
                    authentication_state: isAuthenticated ? "Authenticated" : "Guest",
                    affiliation: currentBillTo?.companyName || "",
                    sales_rep: (session as any).salesperson?.name || "",
                };
                dataLayer.push(gooogleAnalyticsData);
            }
        }
    };
    // XNG-Changes: use custom xUseOverlayBadge than useOverlayBadge
    const { showOverlayBadge, overlayBadges, overlayBadgesWrapperStyles } = xUseOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: false,
        largeImageBadgePositioning: cardView === "large",
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges: false, showImageBadges });

    const hasEnoughInventory = useAppSelector(state => hasEnoughInventorySelector(state, productContext));
    const emptyActionSection = !hasEnoughInventory && menuItemView;

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridContainer
                    extendedStyles={styles.productGridContainer}
                    id={`product_${product.id}`}
                    data-test-selector="productContainer"
                >
                    <StyledWrapper {...styles.imageInfoWrapper}>
                        <ProductGridContainer.Image>
                            {showImage && !showOverlayBadge && (
                                <ProductImage
                                    product={productContext}
                                    onClick={() => {
                                        trackProductRecommendation(true);
                                        // XNG-Change
                                        _GaSelectItem("tp_product_image");
                                    }}
                                    extendedStyles={styles.productImageStyles}
                                />
                            )}
                            {showImage && showOverlayBadge && (
                                <StyledWrapper {...styles.badgeAreaWrapper} data-test-selector="productCarouselBadge">
                                    <ProductImage
                                        extendedStyles={styles.productImageStyles}
                                        product={productContext}
                                        // XNG-Change
                                        onClick={() => {
                                            _GaSelectItem("tp_product_image");
                                        }}
                                    />
                                    <OverlayBadges
                                        badges={overlayBadges}
                                        wrapperCss={overlayBadgesWrapperStyles}
                                        variant={cardView === "medium" ? "relative" : "absolute"}
                                        maxLength={1}
                                    />
                                </StyledWrapper>
                            )}
                        </ProductGridContainer.Image>
                        <ProductGridContainer.Info>
                            <StyledWrapper {...styles.infoSectionWrapper}>
                                {showBrand && product.brand && (
                                    <StyledWrapper {...styles.productBrandWrapper}>
                                        <ProductBrand
                                            brand={product.brand}
                                            extendedStyles={styles.productBrandStyles}
                                        />
                                    </StyledWrapper>
                                )}
                                {showTitle && (
                                    <ProductDescription
                                        product={productContext}
                                        onClick={() => {
                                            trackProductRecommendation(true);
                                            // XNG-Change
                                            _GaSelectItem("tp_product_text");
                                        }}
                                        extendedStyles={styles.productDescriptionStyles}
                                    />
                                )}
                                {showPartNumbers && (
                                    <ProductPartNumbers
                                        productNumber={product.productNumber}
                                        customerProductNumber={product.customerProductNumber}
                                        manufacturerItem={product.manufacturerItem}
                                        uniqueProductCode={product.detail?.upcCode}
                                        extendedStyles={styles.productPartNumbersStyles}
                                    />
                                )}
                                {menuItemView && canSeePrices && (
                                    <ProductPrice product={productContext} extendedStyles={styles.productPriceStyles} />
                                )}
                                {menuItemView && (
                                    <ProductContextAvailability
                                        extendedStyles={styles.productAvailabilityStyles}
                                        hideLink={true}
                                    />
                                )}
                                {showTextBadges && textBadges && (
                                    <ProductTextBadges
                                        badgeList={textBadges}
                                        maxBadges={maximumNumberOfTextBadges ?? 4}
                                    />
                                )}
                                {showImageBadges && imageBadges && (
                                    <ProductImageBadges
                                        badgeList={imageBadges}
                                        maxBadges={maximumNumberOfImageBadges ?? 4}
                                    />
                                )}
                            </StyledWrapper>
                        </ProductGridContainer.Info>
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceAndActionsWrapper}>
                        {!emptyActionSection && (
                            <ProductPriceAndActions
                                onAddToCartClick={() => trackProductRecommendation(false)}
                                showAvailability={!menuItemView}
                                showAddToList={menuItemView ? false : showAddToList}
                                showPrice={menuItemView ? !canSeePrices : showPrice}
                                showDisabled={cardView === "medium"}
                                hideAvailabilityLink={cardView === "medium"}
                                extendedStyles={styles.productPriceAndActionsStyle}
                                showAddToCartIcon={showAddToCartIcon}
                            />
                        )}
                    </StyledWrapper>
                </ProductGridContainer>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
